import { Dialog } from 'ui/shared/containers';
import { useParams } from 'util/reactRouterHooks';
import { push as navigateTo } from 'connected-react-router';
import { getBuildingByUrlSlug, HasThemeId, HasUrlSlug } from 'selectors/buildingsSelectors';
import { CopyThemeForm, CopyThemeProps } from '../copyThemeForm/CopyThemeForm';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { State } from 'reducers';
import { toError } from 'shared/util/toError';
import { useTranslation } from 'react-i18next';
import { getTheme } from 'selectors/themesSelectors';
import { getBuildingsRequest } from 'actions/buildingActions';
import { Building, Theme } from 'shared/models';
import { callApi } from 'apiClient';
import { copyThemeToBuildingSuccess } from 'actions/themeActions';

export type Props = {
    visible: boolean;
};

export const CopyThemeDialog = ({ visible }: Props) => {
    const { t } = useTranslation();

    const params = useParams<HasThemeId & HasUrlSlug>();
    const { themeId, urlSlug } = params;
    const theme = useSelector((state: State) => getTheme(state, params));
    const initialDisplayName = theme && theme.displayName;
    
    const building = useSelector((state: State) => getBuildingByUrlSlug(state, params));
    const buildingId = building && building.id || '';
    
    const [ errorMessage, setErrorMessage ] = useState('');
    const [ submitting, setSubmitting ] = useState(false);
    
    const dispatch = useDispatch();
    
    const handleCopyTheme = useCallback(async (data: CopyThemeProps) => {
        const { selectedBuildingId, selectedBuildingUrlSlug, mode, displayName, destination } = data;
        let finalDisplayName = displayName;
        setErrorMessage('');
        setSubmitting(true);
        const chosenBuildingId = destination === 'this' ? buildingId : selectedBuildingId;
        try {
            const DISPLAY_NAME_MAX_LENGTH = 32;
            if (displayName === "" && mode === "copy") {
                // Supply a default name if the user left it blank
                const availableNameLength = DISPLAY_NAME_MAX_LENGTH - t("X_COPY", [ '' ]).length;
                finalDisplayName = t("X_COPY", [ initialDisplayName!.substring(0, availableNameLength) ]);
            }
            if (selectedBuildingUrlSlug) {
                const theme: Theme = await callApi('PUT', `/api/buildings/${chosenBuildingId}/themes/${themeId}`, { mode, displayName: finalDisplayName });
                dispatch(copyThemeToBuildingSuccess({ buildingId: chosenBuildingId, themeId, mode, theme }));
                dispatch(navigateTo(`/buildings/${selectedBuildingUrlSlug}/themes`));
            }
            else {
                const errorString = t(`THEMES.COPY_DIALOG.NO_BUILDING_SELECTED`);
                setErrorMessage(errorString);
            }
        }
        catch (e) {
            const err = toError(e);
            const errorString = t([ `THEMES.COPY_DIALOG.ERROR.${err.message}`, 'SERVER_ERROR' ]);
            setErrorMessage(errorString);
        }
        finally {
            setSubmitting(false);
        }
    }, [ building, dispatch, t, setSubmitting, setErrorMessage, urlSlug, initialDisplayName ]);
    
    const handleCancel = () => {
        setErrorMessage('');
        dispatch(navigateTo(`/buildings/${urlSlug}/themes`));
    };

    const handleSearch = useCallback((searchText: string, searchResultsCallback: (options: Record<string, unknown>) => void) => {
        // only gets first chunk of buildings related to users search
        // user needs to be more precise in search if building is not in results
        const continueBuildingSearch = (buildings: Building[]) => {
            // call the onSearch callback with buildings we received from saga
            searchResultsCallback({
                success: true,
                results:
                    buildings
                        .filter(b => b.id !== buildingId) // Exclude the current building
                        .map(b => ({
                            name: b.displayName || b.urlSlug,
                            value: b.id,
                        })),
            });
            // tell saga if search should continue
            // always false, we'll re-start onSearch when search occurs next
            return false;
        };
        
        dispatch(getBuildingsRequest({
            searchText,
            continueBuildingSearch,
        }));
    }, [ dispatch, buildingId ]);


    return (
        <Dialog
            id="copy-theme-dialog"
            visible={ visible && !!themeId }
            titleKey={ `THEMES.COPY_DIALOG.TITLE` }
        >
            <CopyThemeForm
                submitting={ submitting }
                buildingId={ buildingId }
                initialDisplayName={ initialDisplayName || '' }
                errorMessage={ errorMessage }
                onSearch={ handleSearch }
                onSubmit={ handleCopyTheme }
                onCancel={ handleCancel }
            />
        </Dialog>
    );
};
